import { Product, Category, Order, Customer, MediaItem, ProductImage } from './types';
import { CANDLE_COLLECTION } from '../data/candles';

const STORAGE_KEY = 'noor_pieces_admin_data_v2';
const EVENT_NAME = 'noor_products_updated';

// Seed Initial Products derived from CANDLE_COLLECTION
const defaultProducts: Product[] = CANDLE_COLLECTION.map((c, index) => {
  const images: ProductImage[] = [
    {
      id: `img-${c.id}-1`,
      url: c.productImage || 'https://images.unsplash.com/photo-1603006905003-be475563bc59?auto=format,compress&fit=crop&w=800&q=75',
      cover: true,
      order: 0,
      thumbnail: c.productImage || 'https://images.unsplash.com/photo-1603006905003-be475563bc59?auto=format,compress&fit=crop&w=200&q=75',
      medium: c.productImage || 'https://images.unsplash.com/photo-1603006905003-be475563bc59?auto=format,compress&fit=crop&w=600&q=75',
    },
    {
      id: `img-${c.id}-2`,
      url: 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?auto=format,compress&fit=crop&w=800&q=75',
      cover: false,
      order: 1,
      thumbnail: 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?auto=format,compress&fit=crop&w=200&q=75',
    },
    {
      id: `img-${c.id}-3`,
      url: 'https://images.unsplash.com/photo-1598501488500-34a942a03305?auto=format,compress&fit=crop&w=800&q=75',
      cover: false,
      order: 2,
      thumbnail: 'https://images.unsplash.com/photo-1598501488500-34a942a03305?auto=format,compress&fit=crop&w=200&q=75',
    }
  ];

  let cat = 'Woody & Amber';
  if (c.id.includes('vanilla')) cat = 'Warm Vanilla';
  if (c.id.includes('lavender')) cat = 'Wild Lavender';
  if (c.id.includes('espresso')) cat = 'Dark Roast Coffee';
  if (c.id.includes('rose')) cat = 'Damask Rose & Oud';

  return {
    id: c.id,
    name: c.name,
    slug: c.id,
    category: cat,
    price: c.price,
    salePrice: c.price > 65 ? c.price - 10 : undefined,
    description: c.description || c.tagline || 'Handcrafted luxury candle.',
    shortDescription: c.tagline,
    fullDescription: c.description,
    notes: `${c.topNotes?.join(', ') || ''} | ${c.heartNotes?.join(', ') || ''} | ${c.baseNotes?.join(', ') || ''}`,
    burnTime: c.burnTime,
    waxType: c.waxType,
    weight: c.weight,
    stock: 45 + index * 12,
    sku: `NOOR-${101 + index}`,
    featured: index < 3,
    status: 'Active',
    images,
    productImage: c.productImage,
    createdAt: new Date(Date.now() - (index + 1) * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - index * 86400000).toISOString(),
    tagline: c.tagline,
    topNotes: c.topNotes,
    heartNotes: c.heartNotes,
    baseNotes: c.baseNotes,
    wickType: c.wickType,
    mood: c.mood,
    colorHex: c.colorHex,
    flameColorHex: c.flameColorHex,
    accentColor: c.accentColor,
    imageBg: c.imageBg,
    vibeStory: c.vibeStory,
  };
});

const defaultCategories: Category[] = [
  { id: 'cat-1', name: 'Woody & Amber', slug: 'woody-amber', description: 'Deep woody warmth and grounding resins', productCount: 2 },
  { id: 'cat-2', name: 'Warm Vanilla', slug: 'warm-vanilla', description: 'Madagascar orchid vanilla and warm tonka bean', productCount: 1 },
  { id: 'cat-3', name: 'Wild Lavender', slug: 'wild-lavender', description: 'High-altitude French lavender and coastal mist', productCount: 1 },
  { id: 'cat-4', name: 'Dark Roast Coffee', slug: 'dark-roast-coffee', description: 'Fresh roasted Arabica beans and dark cacao', productCount: 1 },
  { id: 'cat-5', name: 'Damask Rose & Oud', slug: 'damask-rose-oud', description: 'Velvet rose petals and smoked agarwood', productCount: 1 },
];

const defaultOrders: Order[] = [
  {
    id: 'ORD-9842',
    customerName: 'Eleanor Vance',
    customerEmail: 'eleanor.vance@kyoto.jp',
    total: 136,
    status: 'Delivered',
    date: '2026-07-28',
    itemsCount: 2,
    items: [
      { productId: 'santal-golden-flame', productName: 'Santal & Golden Flame', quantity: 2, price: 68 },
    ],
  },
  {
    id: 'ORD-9843',
    customerName: 'Marcus Dubois',
    customerEmail: 'marcus@zurich.ch',
    total: 64,
    status: 'Shipped',
    date: '2026-07-29',
    itemsCount: 1,
    items: [
      { productId: 'velvet-vanilla-cashmere', productName: 'Velvet Vanilla & Cashmere', quantity: 1, price: 64 },
    ],
  },
  {
    id: 'ORD-9844',
    customerName: 'Dr. Sarah Lin',
    customerEmail: 'sarah.lin@oxford.uk',
    total: 186,
    status: 'Processing',
    date: '2026-07-30',
    itemsCount: 3,
    items: [
      { productId: 'lavender-fog-sea-salt', productName: 'Lavender Fog & Wild Sage', quantity: 3, price: 62 },
    ],
  },
];

const defaultCustomers: Customer[] = [
  { id: 'cust-1', name: 'Eleanor Vance', email: 'eleanor.vance@kyoto.jp', ordersCount: 4, totalSpent: 420, lastOrderDate: '2026-07-28' },
  { id: 'cust-2', name: 'Marcus Dubois', email: 'marcus@zurich.ch', ordersCount: 2, totalSpent: 128, lastOrderDate: '2026-07-29' },
  { id: 'cust-3', name: 'Dr. Sarah Lin', email: 'sarah.lin@oxford.uk', ordersCount: 5, totalSpent: 640, lastOrderDate: '2026-07-30' },
  { id: 'cust-4', name: 'Clara Dubois', email: 'clara.dubois@paris.fr', ordersCount: 3, totalSpent: 310, lastOrderDate: '2026-07-25' },
];

const defaultMedia: MediaItem[] = [
  {
    id: 'med-1',
    name: 'santal-amber-jar.webp',
    url: 'https://images.unsplash.com/photo-1603006905003-be475563bc59?auto=format,compress&fit=crop&w=800&q=75',
    size: '245 KB',
    dimensions: '1200 x 1200',
    format: 'WEBP',
    uploadedAt: '2026-07-20',
  },
  {
    id: 'med-2',
    name: 'vanilla-cashmere-candle.webp',
    url: 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?auto=format,compress&fit=crop&w=800&q=75',
    size: '310 KB',
    dimensions: '1200 x 1200',
    format: 'WEBP',
    uploadedAt: '2026-07-21',
  },
  {
    id: 'med-3',
    name: 'lavender-sage-jar.webp',
    url: 'https://images.unsplash.com/photo-1598501488500-34a942a03305?auto=format,compress&fit=crop&w=800&q=75',
    size: '198 KB',
    dimensions: '1200 x 1200',
    format: 'WEBP',
    uploadedAt: '2026-07-22',
  },
  {
    id: 'med-4',
    name: 'espresso-wood-jar.webp',
    url: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?auto=format,compress&fit=crop&w=800&q=75',
    size: '280 KB',
    dimensions: '1200 x 1200',
    format: 'WEBP',
    uploadedAt: '2026-07-23',
  },
  {
    id: 'med-5',
    name: 'rose-oud-vessel.webp',
    url: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format,compress&fit=crop&w=800&q=75',
    size: '340 KB',
    dimensions: '1200 x 1200',
    format: 'WEBP',
    uploadedAt: '2026-07-24',
  },
];

interface StoreData {
  products: Product[];
  categories: Category[];
  orders: Order[];
  customers: Customer[];
  media: MediaItem[];
}

class ProductStore {
  private data: StoreData;

  constructor() {
    this.data = this.loadFromStorage();
  }

  private loadFromStorage(): StoreData {
    if (typeof window === 'undefined') {
      return {
        products: defaultProducts,
        categories: defaultCategories,
        orders: defaultOrders,
        customers: defaultCustomers,
        media: defaultMedia,
      };
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && Array.isArray(parsed.products) && parsed.products.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to parse admin storage, using defaults:', e);
    }

    const initial = {
      products: defaultProducts,
      categories: defaultCategories,
      orders: defaultOrders,
      customers: defaultCustomers,
      media: defaultMedia,
    };
    this.saveToStorage(initial);
    return initial;
  }

  private saveToStorage(data: StoreData) {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      window.dispatchEvent(new CustomEvent(EVENT_NAME));
    } catch (e) {
      console.error('Failed to save admin storage:', e);
    }
  }

  public subscribe(callback: () => void): () => void {
    if (typeof window === 'undefined') return () => {};
    const handler = () => callback();
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }

  // --- PRODUCTS CRUD ---
  public getProducts(): Product[] {
    return [...this.data.products];
  }

  public getProductById(id: string): Product | undefined {
    return this.data.products.find((p) => p.id === id);
  }

  public saveProduct(product: Product): Product {
    const existingIndex = this.data.products.findIndex((p) => p.id === product.id);
    const updatedProduct = {
      ...product,
      updatedAt: new Date().toISOString(),
    };

    if (existingIndex >= 0) {
      this.data.products[existingIndex] = updatedProduct;
    } else {
      this.data.products.unshift(updatedProduct);
    }

    this.saveToStorage(this.data);
    return updatedProduct;
  }

  public deleteProduct(id: string) {
    this.data.products = this.data.products.filter((p) => p.id !== id);
    this.saveToStorage(this.data);
  }

  public duplicateProduct(id: string): Product | undefined {
    const source = this.getProductById(id);
    if (!source) return undefined;

    const newId = `product-${Date.now()}`;
    const duplicated: Product = {
      ...source,
      id: newId,
      name: `${source.name} (Copy)`,
      slug: `${source.slug}-copy`,
      sku: `${source.sku}-COPY`,
      updatedAt: new Date().toISOString(),
    };

    this.data.products.unshift(duplicated);
    this.saveToStorage(this.data);
    return duplicated;
  }

  // --- CATEGORIES ---
  public getCategories(): Category[] {
    return [...this.data.categories];
  }

  public saveCategory(category: Category) {
    const idx = this.data.categories.findIndex((c) => c.id === category.id);
    if (idx >= 0) {
      this.data.categories[idx] = category;
    } else {
      this.data.categories.push(category);
    }
    this.saveToStorage(this.data);
  }

  public deleteCategory(id: string) {
    this.data.categories = this.data.categories.filter((c) => c.id !== id);
    this.saveToStorage(this.data);
  }

  // --- ORDERS ---
  public getOrders(): Order[] {
    return [...this.data.orders];
  }

  public updateOrderStatus(orderId: string, status: Order['status']) {
    const order = this.data.orders.find((o) => o.id === orderId);
    if (order) {
      order.status = status;
      this.saveToStorage(this.data);
    }
  }

  // --- CUSTOMERS ---
  public getCustomers(): Customer[] {
    return [...this.data.customers];
  }

  // --- MEDIA LIBRARY ---
  public getMedia(): MediaItem[] {
    return [...this.data.media];
  }

  public addMedia(item: MediaItem) {
    this.data.media.unshift(item);
    this.saveToStorage(this.data);
  }

  public deleteMedia(id: string) {
    this.data.media = this.data.media.filter((m) => m.id !== id);
    this.saveToStorage(this.data);
  }
}

export const productStore = new ProductStore();
